import React, {useState, useRef} from 'react';
import {Divider, useTheme, Box} from '@chakra-ui/react';
import {fetchArticles} from '../../endpoints/api';
import {TextA18, TextG12} from '../typography';
import useBookmark from '../../hooks/useBookmark';
import InfiniteScroll from '../InfiniteScroll';
import HomeFeed from '../HomeFeed';
import {BookmarkIcon} from '../../pages/Bookmarks';
import QueryInfiniteScroll from '../QueryInfiniteScroll';
import {getArticleTopItems} from '../../utils/articleTopItems';
import PromptModal from '../Modals/PromptModal';
import dayjs from 'dayjs';
import {getCalenderDate} from '../../utils/date';
import {ArticleType, Event} from '../../types/article';
import {EventRSVPStatus, useSetAttendance} from '../../hooks/useEvent';
import BookmarkButton from '../BookmarkButton';
import {useBannerHeight} from '../../hooks/useTopBarOffset';

export interface Article {
  id: number;
  start_date: string;
  created_at: string;
  image_url: string;
  title: string;
  description: string;
  location: string;
  user: {
    name: string;
  };
  type: string;
  is_bookmarked: boolean;
}

export const BookmarkElement = ({article}: {article: ArticleType}) => {
  const {isBookmarked, handleOnBookmarkClick} = useBookmark(
    article,
    typeof article.type == 'object' ? 'place' : article?.type?.toLowerCase()
  );
  const [isPromptOpen, setPromptOpen] = useState(false);
  const [promptTitle, setPromptTitle] = useState('');
  const dayWords = ['Now', 'Tomorrow', 'Today'];
  const {mutateAsync} = useSetAttendance();

  const handleBookmarkClick = (e) => {
    handleOnBookmarkClick(e);

    // Auto-RSVP as interested when bookmarking events/meetups
    // Only if not already bookmarked and not already INTERESTED or GOING
    if (
      !isBookmarked &&
      (article?.type === 'event' || article?.type === 'meet-up') &&
      'is_walkabout_official' in article &&
      (article as Event)?.rsvp_status?.status_value !== EventRSVPStatus.INTERESTED &&
      (article as Event)?.rsvp_status?.status_value !== EventRSVPStatus.GOING
    ) {
      mutateAsync({
        eventId: article.id,
        prevStatus: (article as Event).rsvp_status?.status_value || EventRSVPStatus.NO_STATUS,
        status: article.is_meetup ? EventRSVPStatus.GOING : EventRSVPStatus.INTERESTED,
        showToast: false,
        showDispatchRSVPModalPopupActions: true,
      });
    }
  };

  const type =
    typeof article.type == 'object' || article.type == null || article.type == 'article'
      ? 'place'
      : article.type;

  const getPromptTitle = () => {
    const dateTimeNow = dayjs().format('YYYY-MM-DD HH:mm:ss').toString();

    if (article.type === 'event' && dateTimeNow < article.start_date_time) {
      const articleDate = getCalenderDate(article.start_date_time).replace(/(:00)/g, '');
      const shouldShowOn = !dayWords.some((day) => articleDate.startsWith(day));
      setPromptTitle(
        `This event is happening ${
          shouldShowOn ? 'on' : ''
        } ${articleDate}. Are you sure you want to remove from Bookmarks?`
      );
      return;
    }

    if (article.type === 'offer' && dateTimeNow < article.ends_at) {
      const articleDate = getCalenderDate(article.ends_at).replace(/(:00)/g, '');
      setPromptTitle(
        `This offer is still valid until ${articleDate}. Are you sure you want to remove from Bookmarks?`
      );
      return;
    }

    setPromptTitle(`Are you sure you want to remove this ${type} from Bookmarks?`);
  };

  return (
    <>
      <BookmarkButton
        isBookmarked={isBookmarked}
        onClick={(e) => {
          if (isBookmarked) {
            e.preventDefault();
            e.stopPropagation();
            getPromptTitle();
            setPromptOpen(true);
          } else {
            e.preventDefault();
            e.stopPropagation();
            handleBookmarkClick(e);
          }
        }}
        position="absolute"
        right="12px"
        top="12px"
        size="36px"
        iconSize="14px"
        zIndex={10}
      />
      <PromptModal
        title={promptTitle}
        declineText={'Keep'}
        acceptText={'Remove'}
        declineCallback={() => setPromptOpen(false)}
        acceptCallback={(e) => {
          handleBookmarkClick(e);
          setPromptOpen(false);
          //window.location.reload();
        }}
        isOpen={isPromptOpen}
      />
    </>
  );
};

const Articles = ({
  children,
  title,
  subtitle,
  type,
  endpoint,
  endpointParams,
  noItems,
  renderArticleType,
  hook = null,
  isEventSortedByHappeningNow = false,
}) => {
  const theme = useTheme();
  const bannerHeight = useBannerHeight();
  // Fix the ref type to accept string values
  const currentDateRef = useRef<string | null>(null);

  // Function to render date header for events
  const renderDateHeader = (date: string) => {
    if (!date) return null;

    try {
      // Parse date as UTC and convert to local time
      const localDate = dayjs.utc(date).local();
      let dateHeader;
      let dateKey; // Key used for grouping

      const today = dayjs().startOf('day');
      const tomorrow = today.add(1, 'day');

      if (localDate.isBefore(today, 'day') || localDate.isSame(today, 'day')) {
        dateHeader = 'Today';
        dateKey = 'today'; // Group all past and today events together
      } else if (localDate.isSame(tomorrow, 'day')) {
        dateHeader = 'Tomorrow';
        dateKey = 'tomorrow';
      } else {
        // Format as "Friday, August 7"
        dateHeader = localDate.format('dddd, MMMM Do');
        dateKey = localDate.format('YYYY-MM-DD');
      }

      // If this is a new date group, render header and return the key
      if (dateKey !== currentDateRef.current) {
        currentDateRef.current = dateKey;
        // Base sticky position (264px) + banner height for proper positioning
        const stickyTop = 264 + bannerHeight;
        return (
          <Box
            py={2}
            px={4}
            position="sticky"
            top={`${stickyTop}px`}
            bg="white"
            zIndex={99}
            borderBottom="1px solid"
            borderColor="lighterGrey"
            mb={2}
            w="100vw"
            ml="-4%">
            <TextA18 fontWeight="bold">{dateHeader}</TextA18>
          </Box>
        );
      }

      return null;
    } catch (error) {
      console.error('Error parsing date', date, error);
      return null;
    }
  };

  return (
    <>
      {title && (
        <TextA18 mb={2} color={theme.colors.darkGrey}>
          {title}
        </TextA18>
      )}
      {subtitle && <TextG12 color={theme.colors.darkGrey}>{subtitle}</TextG12>}
      {(title || subtitle) && <Divider my={3} color={theme.colors.lighterGrey} />}
      {type === 'home' ? (
        <HomeFeed />
      ) : hook ? (
        <QueryInfiniteScroll
          showItems={(item) => {
            // Add date headers for events sorted by "Happening Now"
            const dateHeader = (() => {
              if (isEventSortedByHappeningNow && type === 'event') {
                const eventDate = item.startDate || item.start_date_time || item.date;
                if (!eventDate) return null;

                // renderDateHeader will handle grouping logic internally
                return renderDateHeader(eventDate);
              }
              return null;
            })();

            // For apartments, we need to pass the data as 'place' prop since PlaceCard expects 'place'
            const props =
              type === 'apartment'
                ? {place: item, ...getArticleTopItems({...item, type: item.type || type})}
                : {[type]: item, ...getArticleTopItems({...item, type: item.type || type})};

            return (
              <>
                {dateHeader}
                {React.cloneElement(children, props)}
              </>
            );
          }}
          noItems={noItems}
          hook={hook}
          endpointParams={{...(type !== 'home' && type !== 'article' && {type}), ...endpointParams}}
        />
      ) : (
        <InfiniteScroll
          showItems={(item) => {
            return React.cloneElement(children, {
              [type]: item,
              ...getArticleTopItems({...item, type: item.type || type}),
            });
          }}
          noItems={noItems}
          endpoint={endpoint}
          endpointParams={{...(type !== 'home' && type !== 'article' && {type}), ...endpointParams}}
        />
      )}
    </>
  );
};

Articles.defaultProps = {
  type: 'article',
  title: '',
  subtitle: '',
  endpoint: fetchArticles,
  endpointParams: {},
  noItems: null,
  renderArticleType: true,
  isLoading: true,
};

export default Articles;
